import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Gift, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';

export default function AccountPage() {
  const { user, login, register } = useAuth();
  const { symbol } = useCurrency();
  const navigate = useNavigate();
  const [mode, setMode] = useState('login');
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '' });
  const isMerchantRole = (role) => ['merchant', 'merchant_admin', 'shop_manager'].includes(role);

  // Redirect if already logged in
  React.useEffect(() => {
    if (user) {
      if (isMerchantRole(user.role)) navigate('/merchant', { replace: true });
      else if (user.role === 'admin' || user.role === 'super_admin') navigate('/admin', { replace: true });
      else navigate('/profile', { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === 'login') {
        const u = await login(form.email, form.password);
        toast.success(`Welcome back, ${u.name}!`);
        if (isMerchantRole(u.role)) navigate('/merchant');
        else if (u.role === 'admin' || u.role === 'super_admin') navigate('/admin');
        else navigate('/profile');
      } else {
        await register({ name: form.name, email: form.email, phone: form.phone, password: form.password });
        toast.success('Account created! Please sign in.');
        setMode('login');
        setForm(f => ({ ...f, password: '' }));
      }
    } catch (err) {
      toast.error(err.displayMessage || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <div className="min-h-screen bg-[#FFFCF9] flex" style={{ fontFamily: 'Roboto, sans-serif' }}>
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#561D1A] flex-col items-center justify-center p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-40 h-40 rounded-full bg-[#EC1A23] blur-3xl" />
          <div className="absolute bottom-20 right-10 w-60 h-60 rounded-full bg-[#D4AF37] blur-3xl" />
        </div>
        <Link to="/" className="relative z-10 flex items-center gap-3 mb-10">
          <div className="w-10 h-10 bg-[#EC1A23] rounded-xl flex items-center justify-center">
            <Gift size={22} className="text-white" />
          </div>
          <span className="font-heading text-white text-2xl font-bold">LuxeMall</span>
        </Link>
        <div className="relative z-10 text-center max-w-sm">
          <h2 className="font-heading text-4xl text-white font-bold mb-4">Your Gifts, Your Way</h2>
          <p className="text-white/70 leading-relaxed">
            Access your premium gift vouchers, track purchases, and redeem at over 100 partner stores.
          </p>
          <div className="mt-8 grid grid-cols-2 gap-4">
            {[['100+', 'Partner Stores'], ['50K+', 'Happy Shoppers'], [`${symbol} 50M+`, 'Gifted'], ['4.9/5', 'Rating']].map(([v, l]) => (
              <div key={l} className="bg-white/10 rounded-xl p-4">
                <div className="font-heading text-2xl text-[#D4AF37] font-bold">{v}</div>
                <div className="text-white/60 text-xs mt-1">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-8 h-8 bg-[#EC1A23] rounded-lg flex items-center justify-center">
              <Gift size={18} className="text-white" />
            </div>
            <span className="font-heading text-[#561D1A] text-xl font-bold">LuxeMall</span>
          </div>

          <h1 className="font-heading text-3xl font-bold text-[#561D1A] mb-1">
            {mode === 'login' ? 'Welcome back' : 'Create account'}
          </h1>
          <p className="text-gray-500 text-sm mb-8">
            {mode === 'login' ? 'Sign in to access your vouchers' : 'Join to start gifting today'}
          </p>

          {/* Mode toggle */}
          <div className="flex bg-gray-100 rounded-full p-1 mb-8">
            <button
              onClick={() => setMode('login')}
              className={`flex-1 py-2 rounded-full text-sm font-medium transition-all ${mode === 'login' ? 'bg-white text-[#561D1A] shadow-sm' : 'text-gray-500'}`}
              data-testid="login-tab"
            >Sign In</button>
            <button
              onClick={() => setMode('register')}
              className={`flex-1 py-2 rounded-full text-sm font-medium transition-all ${mode === 'register' ? 'bg-white text-[#561D1A] shadow-sm' : 'text-gray-500'}`}
              data-testid="register-tab"
            >Create Account</button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="auth-form">
            {mode === 'register' && (
              <div>
                <label className="block text-xs text-gray-500 uppercase tracking-wider mb-1.5">Full Name</label>
                <input
                  required
                  value={form.name}
                  onChange={set('name')}
                  placeholder="Jane Doe"
                  className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2.5 text-sm outline-none transition-colors bg-transparent"
                  data-testid="name-input"
                />
              </div>
            )}
            <div>
              <label className="block text-xs text-gray-500 uppercase tracking-wider mb-1.5">Email Address</label>
              <input
                required
                type="email"
                value={form.email}
                onChange={set('email')}
                placeholder="jane@example.com"
                className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2.5 text-sm outline-none transition-colors bg-transparent"
                data-testid="email-input"
              />
            </div>
            {mode === 'register' && (
              <div>
                <label className="block text-xs text-gray-500 uppercase tracking-wider mb-1.5">Phone (optional)</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={set('phone')}
                  placeholder="+254 7XX XXX XXX"
                  className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2.5 text-sm outline-none transition-colors bg-transparent"
                  data-testid="phone-input"
                />
              </div>
            )}
            <div>
              <label className="block text-xs text-gray-500 uppercase tracking-wider mb-1.5">Password</label>
              <div className="relative">
                <input
                  required
                  type={showPwd ? 'text' : 'password'}
                  value={form.password}
                  onChange={set('password')}
                  placeholder="••••••••"
                  className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2.5 text-sm outline-none transition-colors bg-transparent pr-8"
                  data-testid="password-input"
                />
                <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-0 top-2.5 text-gray-400">
                  {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#EC1A23] hover:bg-[#C4121A] disabled:opacity-60 text-white py-3 rounded-full font-medium text-sm transition-all hover:shadow-lg mt-2 flex items-center justify-center gap-2"
              data-testid="auth-submit-btn"
            >
              {loading ? (
                <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> {mode === 'login' ? 'Signing in...' : 'Creating...'}</>
              ) : (
                mode === 'login' ? 'Sign In' : 'Create Account'
              )}
            </button>
          </form>

          <p className="text-center text-xs text-gray-400 mt-8">
            <Link to="/" className="text-[#561D1A] hover:underline">← Back to Shop</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
